import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useFetchCenturyPointLedgerMutation, useFetchCenturyProductLedgerMutation, useFetchCenturyUpiLedgerMutation } from "../../../features/tenant/Century/centuryApi";
import { useFetchAppUserMutation } from "../../../features/tenant/appUsers/appUsersApi";
import { useFetchCenturySkuCsvProductMapMutation } from "../../../features/tenant/skuProductMap/skuProductMapApi";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import SelectComponent from "../../../components/Tenant/SelectComponent/SelectComponent";
import toast from "react-hot-toast";
import { HiOutlineDownload } from "react-icons/hi";
import dayjs from "dayjs";

const formatDate = (val) => (!val || !dayjs(val).isValid() ? "N/A" : dayjs(val).format("DD-MM-YYYY"));

const CenturyLedger = () => {
  const { userId } = useParams();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("point");

  // Point Ledger State
  const ledgerTypes = [
    { name: "My Point", id: "mypoint" },
    { name: "Arjun Point", id: "arjunpoint" },
  ];
  const [selectedType, setSelectedType] = useState(ledgerTypes[0]);

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [ledgerData, setLedgerData] = useState([]);
  const [openingBal, setOpeningBal] = useState(0);
  const [closingBal, setClosingBal] = useState(0);

  // Product Ledger State
  const [productList, setProductList] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [prodFromDate, setProdFromDate] = useState("");
  const [prodToDate, setProdToDate] = useState("");
  const [prodLedgerData, setProdLedgerData] = useState([]);
  const [openingQty, setOpeningQty] = useState(0);
  const [closingQty, setClosingQty] = useState(0);

  // UPI Ledger State
  const [upiFromDate, setUpiFromDate] = useState("");
  const [upiToDate, setUpiToDate] = useState("");
  const [upiLedgerData, setUpiLedgerData] = useState([]);

  const [fetchPointLedger, { isLoading: isPointLoading }] = useFetchCenturyPointLedgerMutation();
  const [fetchProductLedger, { isLoading: isProdLoading }] = useFetchCenturyProductLedgerMutation();
  const [fetchUpiLedger, { isLoading: isUpiLoading }] = useFetchCenturyUpiLedgerMutation();
  const [fetchAppUser] = useFetchAppUserMutation();
  const [fetchSkuCsv] = useFetchCenturySkuCsvProductMapMutation();
  const [userName, setUserName] = useState(location.state?.userData?.name || "N/A");
  const [userType, setUserType] = useState(location.state?.userData?.user_type || location.state?.userData?.userType || "");

  useEffect(() => {
    const getUserData = async () => {
      if (location.state?.userData?.name && (location.state?.userData?.user_type || location.state?.userData?.userType)) return; // Skip if exists in state
      try {
        const response = await fetchAppUser({
          token: sessionStorage.getItem("access_token"),
          tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
          app_user_id: userId,
          type: "user", // Default type
        }).unwrap();
        if (response.success && response.body) {
          setUserName(response.body.name || "N/A");
          if (response.body.user_type || response.body.userType) {
            setUserType(response.body.user_type || response.body.userType);
          }
        }
      } catch (error) {
        console.error("Error fetching user details", error);
      }
    };
    if (userId) getUserData();

    const getProducts = async () => {
      try {
        const response = await fetchSkuCsv({
          token: sessionStorage.getItem("access_token"),
          tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
        }).unwrap();
        if (response.success && response.body?.skuList) {
          const uniqueProducts = Array.from(new Set(response.body.skuList.map(item => item.product_code)))
            .filter(Boolean)
            .map(code => ({ name: code, id: code }));
          setProductList(uniqueProducts);
          if (uniqueProducts.length > 0) setSelectedProduct(uniqueProducts[0]);
        }
      } catch (error) {
        console.error("Error fetching products", error);
      }
    };
    getProducts();
  }, [userId, location.state?.userData?.name]);

  const handleFetch = async () => {
    if (!fromDate || !toDate) {
      toast.error("Please select date range");
      return;
    }
    try {
      const payload = {
        slug: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
        token: sessionStorage.getItem("access_token"),
        body: {
          appUserId: userId,
          type: selectedType.id,
          fromDate,
          toDate,
        },
      };
      const response = await fetchPointLedger(payload).unwrap();
      if (response.success) {
        setLedgerData(response.body.ledger || []);
        setOpeningBal(response.body.openingBal || 0);
        setClosingBal(response.body.closingBal || 0);
        toast.success(response.message);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch ledger");
    }
  };

  const handleProductFetch = async () => {
    if (!prodFromDate || !prodToDate || !selectedProduct) {
      toast.error("Please select product and date range");
      return;
    }
    try {
      const payload = {
        slug: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
        token: sessionStorage.getItem("access_token"),
        body: {
          appUserId: userId,
          productCode: selectedProduct.id,
          fromDate: prodFromDate,
          toDate: prodToDate,
        },
      };
      const response = await fetchProductLedger(payload).unwrap();
      if (response.success) {
        setProdLedgerData(response.body.ledger || []);
        setOpeningQty(response.body.openingQty || 0);
        setClosingQty(response.body.closingQty || 0);
        toast.success(response.message);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch product ledger");
    }
  };

  const handleUpiFetch = async () => {
    if (!upiFromDate || !upiToDate) {
      toast.error("Please select date range");
      return;
    }
    try {
      const payload = {
        slug: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
        token: sessionStorage.getItem("access_token"),
        body: {
          appUserId: userId,
          fromDate: upiFromDate,
          toDate: upiToDate,
        },
      };
      const response = await fetchUpiLedger(payload).unwrap();
      if (response.success) {
        setUpiLedgerData(response.body.ledger || []);
        toast.success(response.message);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch UPI ledger");
    }
  };

  const handleDownloadCsv = () => {
    if (ledgerData.length === 0) {
      toast.error("No data available to download");
      return;
    }

    const csvRows = [];
    csvRows.push("Point Ledger Report");
    csvRows.push(`Name:,${userName}`);
    csvRows.push(`User Type:,${userType ? userType.charAt(0).toUpperCase() + userType.slice(1) : "N/A"}`);
    csvRows.push(`Date:,${formatDate(fromDate)} to ${formatDate(toDate)}`);
    csvRows.push(`Type:,${selectedType.name}`);
    csvRows.push(`Opening Balance:,${Number(openingBal || 0).toFixed(2)}`);
    csvRows.push(`Report Generation Date:,${dayjs().format("DD-MM-YYYY hh:mm:ss A")}`);
    csvRows.push(""); // Spacing line

    const headers = [
      "S.No",
      "Txn Date",
      "Seller Name",
      "Seller Mobile",
      "Buyer Name",
      "Buyer Mobile",
      "Order No",
      "Voucher No",
      "In Points",
      "Out Points",
      "Type",
    ];
    csvRows.push(headers.join(","));

    ledgerData.forEach((row, index) => {
      const rowData = [
        index + 1,
        formatDate(row.txn_date),
        row.seller_name || "N/A",
        row.seller_mobile || "N/A",
        row.buyer_name || "N/A",
        row.buyer_mobile || "N/A",
        row.order_no || "N/A",
        row.voucher_no || "N/A",
        row.in_points || 0,
        row.out_points || 0,
        row.type || "N/A",
      ];
      csvRows.push(rowData.map(v => `"${String(v).replace(/"/g, '""')}"`).join(","));
    });

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Century_Point_Ledger_${userName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadProductCsv = () => {
    if (prodLedgerData.length === 0) {
      toast.error("No data available to download");
      return;
    }

    const csvRows = [];
    csvRows.push("Product Ledger");
    csvRows.push(`Name:,${userName}`);
    csvRows.push(`Date:,${formatDate(prodFromDate)} to ${formatDate(prodToDate)}`);
    csvRows.push(`Product:,${selectedProduct.name}`);
    csvRows.push("");

    const headers = [
      "S.No",
      "Txn Date",
      "Product Code",
      "Order No",
      "Seller Name",
      "Seller Mobile",
      "Buyer Name",
      "Buyer Mobile",
      "In Qty",
      "Out Qty",
    ];
    csvRows.push(headers.join(","));

    prodLedgerData.forEach((row, index) => {
      const rowData = [
        index + 1,
        formatDate(row.txn_date),
        row.product_code || "N/A",
        row.order_no || "N/A",
        row.seller_name || "N/A",
        row.seller_mobile || "N/A",
        row.buyer_name || "N/A",
        row.buyer_mobile || "N/A",
        row.in_qty || 0,
        row.out_qty || 0,
      ];
      csvRows.push(rowData.map(v => `"${String(v).replace(/"/g, '""')}"`).join(","));
    });

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Century_Product_Ledger_${userName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadUpiCsv = () => {
    if (upiLedgerData.length === 0) {
      toast.error("No data available to download");
      return;
    }

    const csvRows = [];
    csvRows.push("UPI Ledger");
    csvRows.push(`Name:,${userName}`);
    csvRows.push(`Date:,${formatDate(upiFromDate)} to ${formatDate(upiToDate)}`);
    csvRows.push("");

    const headers = [
      "S.No",
      "Name",
      "Mobile",
      "Redemption Date",
      "Transaction Date",
      "Points",
      "Total Amount",
      "TDS",
      "Net Transferred Amount",
      "City",
      "State",
      "Pincode",
      "Address",
    ];
    csvRows.push(headers.join(","));

    upiLedgerData.forEach((row, index) => {
      const rowData = [
        index + 1,
        row.name || "N/A",
        row.mobile || "N/A",
        formatDate(row.redemption_date),
        formatDate(row.transaction_date),
        row.points || 0,
        row.total_amount || 0,
        row.tds || 0,
        row.net_transferred_amount || 0,
        row.city || "N/A",
        row.state || "N/A",
        row.pincode || "N/A",
        row.address1 || "N/A",
      ];
      csvRows.push(rowData.map(v => `"${String(v).replace(/"/g, '""')}"`).join(","));
    });

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Century_UPI_Ledger_${userName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns = [
    {
      name: "S.No",
      cell: (row, index) => index + 1,
      width: "70px",
    },
    {
      name: "Txn Date",
      cell: (row) => formatDate(row.txn_date),
      sortable: true,
    },
    {
      name: "Seller Name",
      cell: (row) => row.seller_name || "N/A",
      sortable: true,
    },
    {
      name: "Seller Mobile",
      cell: (row) => row.seller_mobile || "N/A",
      sortable: true,
    },
    {
      name: "Buyer Name",
      cell: (row) => row.buyer_name || "N/A",
      sortable: true,
    },
    {
      name: "Buyer Mobile",
      cell: (row) => row.buyer_mobile || "N/A",
      sortable: true,
    },
    {
      name: "Order No",
      cell: (row) => row.order_no || "N/A",
      sortable: true,
    },
    {
      name: "Voucher No",
      cell: (row) => row.voucher_no || "N/A",
      sortable: true,
    },
    {
      name: "In Points",
      cell: (row) => <span className="text-green-600 font-semibold">{row.in_points}</span>,
      sortable: true,
    },
    {
      name: "Out Points",
      cell: (row) => <span className="text-red-600 font-semibold">{row.out_points}</span>,
      sortable: true,
    },
    {
      name: "Type",
      cell: (row) => <span className="capitalize">{row.type}</span>,
      sortable: true,
    },
  ];

  return (
    <div className="flex w-full bg-gray-100 h-full justify-center">
      <div className="w-full">
        <div className="border-b bg-white border-gray-300 pl-6 py-2 shadow-sm text-xl font-bold flex justify-between items-center pr-6">
          Century Ledger
          <LinksComponent />
        </div>

        <div className="p-4">
          <div className="bg-white rounded-xl shadow-lg transition-all duration-300">
            <div className="flex border-b overflow-hidden rounded-t-xl">
              {["point", "product", "upi"].map((tab, idx, arr) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`flex-1 py-4 text-center font-bold text-sm uppercase tracking-wider transition-all duration-200 ${
                    activeTab === tab
                      ? "bg-blue-600 text-white shadow-inner"
                      : "bg-gray-50 text-gray-500 hover:bg-gray-100"
                  } ${idx === 0 ? "rounded-tl-xl" : ""} ${idx === arr.length - 1 ? "rounded-tr-xl" : ""}`}
                >
                  {tab} Ledger
                </button>
              ))}
            </div>

            <div className="p-6">
              {activeTab === "point" && (
                <div className="space-y-6 animate-fadeIn">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-end">
                    <div className="space-y-1">
                      <label className="text-xs font-bold text-gray-500 uppercase">Ledger Type</label>
                      <SelectComponent
                        selected={selectedType}
                        setSelected={setSelectedType}
                        dataArr={ledgerTypes}
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-xs font-bold text-gray-500 uppercase">From Date</label>
                      <input
                        type="date"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-bold text-gray-500 uppercase">To Date</label>
                      <input
                        type="date"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                      />
                    </div>
                    <button
                      onClick={handleFetch}
                      disabled={isPointLoading}
                      className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg shadow-md hover:shadow-lg transition-all transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isPointLoading ? "Fetching..." : "Fetch Ledger"}
                    </button>
                  </div>

                  {ledgerData.length > 0 && (
                    <div className="flex justify-end">
                      <button
                        onClick={handleDownloadCsv}
                        className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg shadow-md transition-all transform active:scale-95"
                      >
                        <HiOutlineDownload size={20} />
                        Download Point CSV
                      </button>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-2xl border border-blue-100 flex flex-col justify-center items-center group hover:shadow-md transition-all">
                      <p className="text-xs font-bold text-blue-400 uppercase tracking-widest mb-1">Opening Balance</p>
                      <p className="text-3xl font-black text-blue-800 tracking-tight">{openingBal}</p>
                    </div>
                    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-6 rounded-2xl border border-emerald-100 flex flex-col justify-center items-center group hover:shadow-md transition-all">
                      <p className="text-xs font-bold text-emerald-400 uppercase tracking-widest mb-1">Closing Balance</p>
                      <p className="text-3xl font-black text-emerald-800 tracking-tight">{closingBal}</p>
                    </div>
                  </div>

                  <div className="rounded-xl border border-gray-100 overflow-hidden shadow-sm">
                    <DataTableComponent
                      columns={columns}
                      data={ledgerData}
                      pagination={false}
                    />
                  </div>
                </div>
              )}

              {activeTab === "product" && (
                <div className="space-y-6 animate-fadeIn">
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-6 items-end">
                    <div className="space-y-1 md:col-span-2">
                      <label className="text-xs font-bold text-gray-500 uppercase">Product</label>
                      <SelectComponent
                        selected={selectedProduct}
                        setSelected={setSelectedProduct}
                        dataArr={productList}
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-xs font-bold text-gray-500 uppercase">From Date</label>
                      <input
                        type="date"
                        value={prodFromDate}
                        onChange={(e) => setProdFromDate(e.target.value)}
                        className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-bold text-gray-500 uppercase">To Date</label>
                      <input
                        type="date"
                        value={prodToDate}
                        onChange={(e) => setProdToDate(e.target.value)}
                        className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                      />
                    </div>
                    <button
                      onClick={handleProductFetch}
                      disabled={isProdLoading}
                      className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg shadow-md hover:shadow-lg transition-all transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isProdLoading ? "Fetching..." : "Fetch Ledger"}
                    </button>
                  </div>

                  {prodLedgerData.length > 0 && (
                    <div className="flex justify-end">
                      <button
                        onClick={handleDownloadProductCsv}
                        className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg shadow-md transition-all transform active:scale-95"
                      >
                        <HiOutlineDownload size={20} />
                        Download Product CSV
                      </button>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-2xl border border-blue-100 flex flex-col justify-center items-center group hover:shadow-md transition-all">
                      <p className="text-xs font-bold text-blue-400 uppercase tracking-widest mb-1">Opening Qty</p>
                      <p className="text-3xl font-black text-blue-800 tracking-tight">{openingQty}</p>
                    </div>
                    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-6 rounded-2xl border border-emerald-100 flex flex-col justify-center items-center group hover:shadow-md transition-all">
                      <p className="text-xs font-bold text-emerald-400 uppercase tracking-widest mb-1">Closing Qty</p>
                      <p className="text-3xl font-black text-emerald-800 tracking-tight">{closingQty}</p>
                    </div>
                  </div>

                  <div className="rounded-xl border border-gray-100 overflow-hidden shadow-sm">
                    <DataTableComponent
                      columns={[
                        {
                          name: "S.No",
                          cell: (row, index) => index + 1,
                          width: "70px",
                        },
                        {
                          name: "Txn Date",
                          cell: (row) => formatDate(row.txn_date),
                          sortable: true,
                        },
                        {
                          name: "Product Code",
                          cell: (row) => row.product_code || "N/A",
                          sortable: true,
                        },
                        {
                          name: "Order No",
                          cell: (row) => row.order_no || "N/A",
                          sortable: true,
                        },
                        {
                          name: "Seller Name",
                          cell: (row) => row.seller_name || "N/A",
                          sortable: true,
                        },
                        {
                          name: "Seller Mobile",
                          cell: (row) => row.seller_mobile || "N/A",
                          sortable: true,
                        },
                        {
                          name: "Buyer Name",
                          cell: (row) => row.buyer_name || "N/A",
                          sortable: true,
                        },
                        {
                          name: "Buyer Mobile",
                          cell: (row) => row.buyer_mobile || "N/A",
                          sortable: true,
                        },
                        {
                          name: "In Qty",
                          cell: (row) => <span className="text-green-600 font-semibold">{row.in_qty}</span>,
                          sortable: true,
                        },
                        {
                          name: "Out Qty",
                          cell: (row) => <span className="text-red-600 font-semibold">{row.out_qty}</span>,
                          sortable: true,
                        },
                      ]}
                      data={prodLedgerData}
                      pagination={false}
                    />
                  </div>
                </div>
              )}

              {activeTab === "upi" && (
                <div className="space-y-6 animate-fadeIn">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
                    <div className="space-y-1">
                      <label className="text-xs font-bold text-gray-500 uppercase">From Date</label>
                      <input
                        type="date"
                        value={upiFromDate}
                        onChange={(e) => setUpiFromDate(e.target.value)}
                        className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-bold text-gray-500 uppercase">To Date</label>
                      <input
                        type="date"
                        value={upiToDate}
                        onChange={(e) => setUpiToDate(e.target.value)}
                        className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 transition-all outline-none"
                      />
                    </div>
                    <button
                      onClick={handleUpiFetch}
                      disabled={isUpiLoading}
                      className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg shadow-md hover:shadow-lg transition-all transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isUpiLoading ? "Fetching..." : "Fetch Ledger"}
                    </button>
                  </div>

                  {upiLedgerData.length > 0 && (
                    <div className="flex justify-end">
                      <button
                        onClick={handleDownloadUpiCsv}
                        className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg shadow-md transition-all transform active:scale-95"
                      >
                        <HiOutlineDownload size={20} />
                        Download UPI CSV
                      </button>
                    </div>
                  )}

                  <div className="rounded-xl border border-gray-100 overflow-hidden shadow-sm">
                    <DataTableComponent
                      columns={[
                        {
                          name: "S.No",
                          cell: (row, index) => index + 1,
                          width: "70px",
                        },
                        {
                          name: "Name",
                          cell: (row) => row.name || "N/A",
                          sortable: true,
                        },
                        {
                          name: "Mobile",
                          cell: (row) => row.mobile || "N/A",
                          sortable: true,
                        },
                        {
                          name: "Redemption Date",
                          cell: (row) => formatDate(row.redemption_date),
                          sortable: true,
                        },
                        {
                          name: "Transaction Date",
                          cell: (row) => formatDate(row.transaction_date),
                          sortable: true,
                        },
                        {
                          name: "Points",
                          cell: (row) => <span className="font-semibold">{row.points}</span>,
                          sortable: true,
                        },
                        {
                          name: "Total Amt",
                          cell: (row) => <span className="text-blue-600 font-semibold">{row.total_amount}</span>,
                          sortable: true,
                        },
                        {
                          name: "TDS",
                          cell: (row) => <span className="text-amber-600 font-semibold">{row.tds}</span>,
                          sortable: true,
                        },
                        {
                          name: "Net Transferred",
                          cell: (row) => <span className="text-green-600 font-bold">{row.net_transferred_amount}</span>,
                          sortable: true,
                        },
                      ]}
                      data={upiLedgerData}
                      pagination={false}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CenturyLedger;
